/**
 * @module employee_attribute.actions
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import { graphqlRequest, hasGraphQLNetworkError } from '~/adapters/graphqlRequest';
import {
  CompanyAttributeValuesDocument,
  CreateEmployeeAttributeDocument,
  CreateEmployeeAttributeMutationResult,
  FetchEmployeeAttributesDocument,
  FetchEmployeeAttributesQueryResult,
  RemoveEmployeeAttributeDocument,
  RemoveEmployeeAttributeMutationResult,
  UpdateEmployeeAttributeDocument,
  UpdateEmployeeAttributeMutationResult,
} from '~/gql/graphql';
import * as transformers from '../transformers';
import * as schemas from '../schemas';
import {
  CreateEmployeeAttributeParams,
  EmployeeAttributeDict,
  EmployeeAttributeResult,
  EmployeeAttributesResult,
  FetchEmployeeAttributeValuesParams,
  FetchSurveyTypeAttributesParams,
  ModifyEmployeeAttributeParams,
} from '~/interfaces/employee.interface';
import { RootState } from '../store';
import { attributesQueries } from '../queries/attributes.queries';

const fetchEmployeeAttributes = createAsyncThunk<
  EmployeeAttributeDict,
  { companyId: number; includePrivateAttributes?: boolean },
  { state: RootState }
>(
  'attributes/fetchEmployeeAttributes',
  async ({ companyId, includePrivateAttributes = false }, { getState, rejectWithValue }) => {
    const account = getState().account;

    const res = await graphqlRequest<FetchEmployeeAttributesQueryResult>(FetchEmployeeAttributesDocument, {
      companyId,
      includePrivateAttributes,
    });
    if (hasGraphQLNetworkError(res)) rejectWithValue(res.error);

    const rawAttributes = res.data?.getCompanyConfigDetails?.attributes;
    const employeeAttributes = transformers.employeeAttributesFrom(rawAttributes, {
      defaultLang: account.lang,
    });

    // This is a bit of a trick. We've defined a company schema to include a
    // property called `employeeAttributes`. All the employee attributes retreived
    // here are for a specific company which include a `companyId` key. Here,
    // we're passing the data through the company schema, using that companyId,
    // as a means of both normalizing the employeeAttributes entities as well
    // as appending the relationship they have with this company entity.
    const company = {
      id: companyId,
      employeeAttributes,
    };
    const normalizedData = normalize(company, schemas.company);

    return normalizedData;
  },
);

const fetchEmployeeAttributeValues = createAsyncThunk<
  EmployeeAttributesResult,
  FetchEmployeeAttributeValuesParams,
  { state: RootState }
>(
  'attributes/fetchEmployeeAttributeValues',
  async (
    {
      employeeAttributeId,
      companyId = null,
      surveyTypeId = null,
      includeSnapshotValues = true,
      surveyIds = null,
      onlyEnabledEmployees = false,
    },
    { getState, rejectWithValue },
  ) => {
    const state = getState();
    const account = state.account;
    const languageId = state.entities.languages.codeIds[account.lang] || account.langId;

    const res = await graphqlRequest<any>(CompanyAttributeValuesDocument, {
      attributeId: employeeAttributeId,
      companyId: companyId ? companyId : account.companyId,
      languageId, // TODO: attribute values do not have a language, so this is kind of redundant
      surveyTypeId,
      includeSnapshotValues,
      surveyIds,
      onlyEnabledEmployees,
    });
    if (hasGraphQLNetworkError(res)) rejectWithValue(res.error);

    const employeeAttribute = {
      id: employeeAttributeId,
      values: res.data?.getCompanyAttributeValues.attributeValues,
    };

    return {
      companyId,
      entities: {
        employeeAttributes: [employeeAttribute],
      },
    };
  },
);

const modifyEmployeeAttribute = createAsyncThunk<
  EmployeeAttributesResult,
  ModifyEmployeeAttributeParams,
  { state: RootState }
>(
  'attributes/modifyEmployeeAttribute',
  async (
    { employeeAttributeId, type, translations, isRequired, isUnique, isPrivate, isPrimary, isPhone, isArchived },
    { getState, rejectWithValue },
  ) => {
    const state = getState();
    const account = state.account;
    const languageCodeIds = state.entities.languages.codeIds;
    const attributeNames = transformers.employeeAttributeTranslationsToNames(translations, languageCodeIds);

    const res = await graphqlRequest<UpdateEmployeeAttributeMutationResult>(UpdateEmployeeAttributeDocument, {
      companyId: account.companyId,
      employeeAttributeId,
      attribute: {
        // should match the AttributeUpdateInput type on the server
        names: attributeNames,
        isRequired,
        isUnique,
        isPrivate,
        isReportingAttribute: isPrimary,
        isPhone,
        isArchived,
      },
    });

    if (hasGraphQLNetworkError(res)) rejectWithValue(res.error);

    const rawAttribute = res.data?.updateAttribute;
    const employeeAttribute = transformers.employeeAttributeFrom(rawAttribute, {
      defaultLang: account.lang,
    });

    return {
      companyId: account.companyId,
      entities: {
        employeeAttributes: [employeeAttribute],
      },
    };
  },
);

const createEmployeeAttribute = createAsyncThunk<
  { companyId: number; employeeAttribute: EmployeeAttributeResult },
  CreateEmployeeAttributeParams,
  { state: RootState }
>(
  'attributes/removeEmployeeAttribute',
  // @ts-ignore @TODO Epic WT-21600
  async (
    { type, translations, isRequired, isUnique, isPrivate, isPrimary, isPhone },
    { getState, rejectWithValue },
  ) => {
    const state = getState();
    const account = state.account;
    const languageCodeIds = state.entities.languages.codeIds;
    const attributeNames = transformers.employeeAttributeTranslationsToNames(translations, languageCodeIds);

    const res = await graphqlRequest<CreateEmployeeAttributeMutationResult>(CreateEmployeeAttributeDocument, {
      companyId: account.companyId,
      attribute: {
        // should match the AttributeInput type on the server
        names: attributeNames,
        type: {
          // should match the AttributeTypeInput type on the server
          name: type,
        },
        isRequired,
        isUnique,
        isPrivate,
        isReportingAttribute: isPrimary,
        isPhone,
      },
    });

    if (hasGraphQLNetworkError(res)) rejectWithValue(res.error);

    const rawAttribute = res.data?.addAttribute;
    const employeeAttribute = transformers.employeeAttributeFrom(rawAttribute, {
      defaultLang: account.lang,
    });

    return {
      companyId: account.companyId,
      employeeAttribute,
    };
  },
);

const removeEmployeeAttribute = createAsyncThunk<
  { companyId: number; employeeAttributeId: number },
  { employeeAttributeId: number },
  { state: RootState }
  // @ts-ignore @TODO Epic WT-21600
>('attributes/createEmployeeAttribute', async ({ employeeAttributeId }, { getState, rejectWithValue }) => {
  const state = getState();
  const companyId = state.account.companyId;
  const res = await graphqlRequest<RemoveEmployeeAttributeMutationResult>(RemoveEmployeeAttributeDocument, {
    companyId,
    employeeAttributeId,
  });

  if (hasGraphQLNetworkError(res)) rejectWithValue(res.error);

  return {
    companyId,
    employeeAttributeId,
  };
});

/**
 * Get employee attributes that are visible to a user by their survey type permission.
 */
const fetchSurveyTypeAttributes = createAsyncThunk<
  { surveyTypeId: number; attributeIds: number[] },
  FetchSurveyTypeAttributesParams
>(
  'attribute/fetchSurveyTypeAttributes',
  async ({ companyId, surveyTypeId, includePrivateAttributes = false }, { rejectWithValue }) => {
    const surveyTypeAttributes = await attributesQueries.fetchEmployeeAttributes({
      companyId: companyId as number,
      surveyTypeId,
      includePrivateAttributes,
    });

    const attributeIds = surveyTypeAttributes.map((attr) => attr.id);

    return {
      surveyTypeId,
      attributeIds,
    };
  },
);

export const employeeAttributeActions = {
  fetchEmployeeAttributes,
  fetchEmployeeAttributeValues,
  modifyEmployeeAttribute,
  createEmployeeAttribute,
  removeEmployeeAttribute,
  fetchSurveyTypeAttributes,
};
